import {
  EventConfigEnriched,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import {
  AndCondition,
  AndConditionConditionsInner,
  ContainsAnyOfCondition,
  EqualsCondition,
  EventFilterRequestFilterCondition,
  FieldOperandTypeEnum,
  GreaterThanCondition,
  GreaterThanOrEqualsCondition,
  IsInCondition,
  IsNullCondition,
  LessThanCondition,
  LessThanOrEqualsCondition,
  Lhs,
  LikeCondition,
  LiteralOperandTypeEnum,
  MultipleOfCondition,
  NotEqualsCondition,
  OrCondition,
  Rhs,
  Value,
} from "src/clients/features-retrieve";
import {
  validateInteger,
  validateNumber,
  validateString,
} from "src/datasets/DatasetTable/validators";
import { DEFAULT_JUNCTION } from "src/eventsCatalogue/filters/constants";
import {
  EventFilterCondition,
  EventFilterFormData,
  Junction,
} from "src/eventsCatalogue/filters/types";

export const getEventProperties = (
  eventSchema: EventConfigEnriched | undefined,
) => {
  if (!eventSchema?.properties) {
    return [];
  }

  return Object.entries(eventSchema.properties)
    .map(([name, property]) => {
      // Determine the actual datatype based on type and enum_schema
      let datatype = property.type;

      // If type is "string" and has enum_schema, it's actually an ENUM
      if (
        property.type === PropertyDefinitionOutputTypeEnum.STRING &&
        property.enum_schema
      ) {
        datatype = PropertyDefinitionOutputTypeEnum.ENUM;
      }
      // If type is "tags" and has enum_schema, it's TAGS (already correct)
      // TAGS type already has enum_schema, so no change needed

      return {
        name,
        displayName: property.display_name ?? "",
        datatype,
      };
    })
    .toSorted((a, b) => a.displayName.localeCompare(b.displayName ?? ""));
};

/**
 * Builds a default filter condition
 * Update to use backend enum operator when API is ready
 */
export const buildFilterCondition = (
  junction: Junction = DEFAULT_JUNCTION,
): EventFilterCondition => ({
  junction,
  field: "",
  operator: "eq",
  value: "",
});

/**
 * Checks if a filter condition is valid (has a field selected)
 */
export const isValidCondition = (condition: EventFilterCondition): boolean => {
  return condition.field !== "" && condition.field !== null;
};

export const parseJsonArray = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const parseDateRange = (value: string | undefined) => {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    return {
      from: parsed.from ? new Date(parsed.from) : undefined,
      to: parsed.to ? new Date(parsed.to) : undefined,
    };
  } catch {
    return undefined;
  }
};

/**
 * Strips surrounding quotes (single or double) from a string value.
 * Handles both "string" and 'string' formats, but only removes quotes if they match on both ends.
 */
const stripSurroundingQuotes = (value: string): string => {
  if (!value || value.length < 2) return value;

  const first = value[0];
  if ((first === '"' || first === "'") && value[value.length - 1] === first) {
    return value.slice(1, -1);
  }

  return value;
};

export const convertFormDataToBackendFilter = (
  formData: EventFilterFormData,
  eventProperties?: Array<{
    name: string;
    datatype: PropertyDefinitionOutputTypeEnum;
  }>,
): EventFilterRequestFilterCondition | null => {
  const groups = formData.filter_condition?.conditions;
  if (!groups || groups.length === 0) {
    return null;
  }

  // Filter out invalid conditions and convert to backend format
  const validGroups = groups
    .map((group) => ({
      ...group,
      conditions: group.conditions.filter(isValidCondition),
    }))
    .filter((group) => group.conditions.length > 0);

  if (validGroups.length === 0) {
    return null;
  }

  // Helper functions to create operands
  const createFieldOperand = (field: string): Lhs =>
    ({
      type: FieldOperandTypeEnum.FIELD,
      value: field,
    }) as Lhs;

  const createLiteralOperand = (value: string): Rhs =>
    ({
      type: LiteralOperandTypeEnum.LITERAL,
      value,
    }) as Rhs;

  const createFieldValue = (field: string): Value =>
    ({
      type: FieldOperandTypeEnum.FIELD,
      value: field,
    }) as Value;

  // Helper function to get the field type
  const getFieldType = (
    fieldName: string,
  ): PropertyDefinitionOutputTypeEnum | undefined => {
    if (!eventProperties) {
      return undefined;
    }
    const property = eventProperties.find((p) => p.name === fieldName);
    return property?.datatype;
  };

  // Helper function to get the field name with .id suffix for relation types
  const getFieldName = (fieldName: string): string => {
    const fieldType = getFieldType(fieldName);
    // We are only filtering on the id of the relation field
    if (fieldType === PropertyDefinitionOutputTypeEnum.RELATION) {
      return `${fieldName}.id`;
    }
    return fieldName;
  };

  // Convert each condition to backend format
  const convertCondition = (
    condition: EventFilterCondition,
  ): AndConditionConditionsInner | null => {
    if (!condition.field || !condition.operator) {
      return null;
    }

    const fieldName = getFieldName(condition.field);
    const lhs = createFieldOperand(fieldName);

    // Handle is_none (is_null)
    if (condition.operator === "is_none") {
      return {
        type: "is_null" as const,
        value: createFieldValue(fieldName),
      } as IsNullCondition & { type: "is_null" };
    }

    // Handle empty value for non-null operators
    if (!condition.value) {
      return null;
    }

    // Handle date/datetime fields with date range JSON strings
    const fieldType = getFieldType(condition.field);
    const isDateOrDatetimeField =
      fieldType === PropertyDefinitionOutputTypeEnum.DATE ||
      fieldType === PropertyDefinitionOutputTypeEnum.DATETIME;

    if (isDateOrDatetimeField) {
      // Try to parse as date range JSON
      const dateRange = parseDateRange(condition.value);
      if (dateRange?.from || dateRange?.to) {
        // Convert date range to AND condition: field >= from AND field <= to
        const conditions: AndConditionConditionsInner[] = [];

        if (dateRange.from && !isNaN(dateRange.from.getTime())) {
          conditions.push({
            type: "gte" as const,
            lhs,
            rhs: createLiteralOperand(dateRange.from.toISOString()),
          } as GreaterThanOrEqualsCondition & { type: "gte" });
        }

        if (dateRange.to && !isNaN(dateRange.to.getTime())) {
          conditions.push({
            type: "lte" as const,
            lhs,
            rhs: createLiteralOperand(dateRange.to.toISOString()),
          } as LessThanOrEqualsCondition & { type: "lte" });
        }

        if (conditions.length === 0) {
          return null;
        }

        if (conditions.length === 1) {
          return conditions[0];
        }

        // Combine multiple conditions with AND
        return {
          type: "and" as const,
          conditions,
        } as AndCondition & { type: "and" };
      }
    }

    const rhs = createLiteralOperand(condition.value);

    // Handle all operators
    switch (condition.operator) {
      case "eq":
        return {
          type: "eq" as const,
          lhs,
          rhs,
        } as EqualsCondition & { type: "eq" };

      case "ne":
        return {
          type: "ne" as const,
          lhs,
          rhs,
        } as NotEqualsCondition & { type: "ne" };

      case "gt":
        return {
          type: "gt" as const,
          lhs,
          rhs,
        } as GreaterThanCondition & { type: "gt" };

      case "ge":
      case "gte":
        // Handle both frontend "ge" and backend "gte" for robustness
        return {
          type: "gte" as const,
          lhs,
          rhs,
        } as GreaterThanOrEqualsCondition & { type: "gte" };

      case "lt":
        return {
          type: "lt" as const,
          lhs,
          rhs,
        } as LessThanCondition & { type: "lt" };

      case "le":
      case "lte":
        // Handle both frontend "le" and backend "lte" for robustness
        return {
          type: "lte" as const,
          lhs,
          rhs,
        } as LessThanOrEqualsCondition & { type: "lte" };

      case "multiple_of":
        return {
          type: "multiple_of" as const,
          lhs,
          rhs,
        } as MultipleOfCondition & { type: "multiple_of" };

      case "like":
        return {
          type: "like" as const,
          lhs,
          rhs,
        } as LikeCondition & { type: "like" };

      case "is_in":
      case "one_of":
        return {
          type: "is_in" as const,
          lhs,
          rhs,
        } as IsInCondition & { type: "is_in" };

      case "str_contains": {
        return {
          type: "like" as const,
          lhs,
          rhs: createLiteralOperand(
            `%${stripSurroundingQuotes(condition.value)}%`,
          ),
        } as LikeCondition & { type: "like" };
      }

      case "contains_any_of":
        return {
          type: "contains_any_of" as const,
          lhs,
          rhs,
        } as ContainsAnyOfCondition & { type: "contains_any_of" };

      case "contains": {
        // For TAGS, map to contains_any_of
        // For URL, map to LIKE with pattern
        const fieldType = getFieldType(condition.field);
        if (fieldType === PropertyDefinitionOutputTypeEnum.TAGS) {
          return {
            type: "contains_any_of" as const,
            lhs,
            rhs: createLiteralOperand(stripSurroundingQuotes(condition.value)),
          } as ContainsAnyOfCondition & { type: "contains_any_of" };
        }
        // For URL contains, map to LIKE with pattern
        return {
          type: "like" as const,
          lhs,
          rhs: createLiteralOperand(
            `%${stripSurroundingQuotes(condition.value)}%`,
          ),
        } as LikeCondition & { type: "like" };
      }

      case "starts_with": {
        return {
          type: "like" as const,
          lhs,
          rhs: createLiteralOperand(
            `${stripSurroundingQuotes(condition.value)}%`,
          ),
        } as LikeCondition & { type: "like" };
      }

      case "ends_with": {
        return {
          type: "like" as const,
          lhs,
          rhs: createLiteralOperand(
            `%${stripSurroundingQuotes(condition.value)}`,
          ),
        } as LikeCondition & { type: "like" };
      }

      default:
        // Default to eq for unknown operators
        return {
          type: "eq" as const,
          lhs,
          rhs,
        } as EqualsCondition & { type: "eq" };
    }
  };

  // Convert groups to backend conditions
  // One group per condition - nested conditions should only be created when combining multiple groups
  const backendConditions = validGroups
    .map((group) => {
      const convertedConditions = group.conditions
        .map(convertCondition)
        .filter((c): c is AndConditionConditionsInner => c !== null);

      if (convertedConditions.length === 0) {
        return null;
      }

      // For a single group, return the condition(s) directly without nesting
      // Nested structures will only be created when combining multiple groups
      if (convertedConditions.length === 1) {
        return convertedConditions[0];
      }

      // If a group has multiple conditions, combine them using each condition's junction
      // Start with the first condition, then combine each subsequent condition using its junction
      let result: AndConditionConditionsInner = convertedConditions[0];

      for (let i = 1; i < convertedConditions.length; i++) {
        const condition = group.conditions[i];
        const junction = condition?.junction || "and";
        const nextCondition = convertedConditions[i];

        if (junction === "or") {
          result = {
            type: "or" as const,
            conditions: [result, nextCondition],
          } as OrCondition & { type: "or" };
        } else {
          result = {
            type: "and" as const,
            conditions: [result, nextCondition],
          } as AndCondition & { type: "and" };
        }
      }

      return result;
    })
    .filter((c): c is AndConditionConditionsInner => c !== null);

  if (backendConditions.length === 0) {
    return null;
  }

  if (backendConditions.length === 1) {
    return backendConditions[0] as EventFilterRequestFilterCondition;
  }

  let result: EventFilterRequestFilterCondition = backendConditions[0];

  for (let i = 1; i < backendConditions.length; i++) {
    const groupCondition = backendConditions[i];
    const currentGroup = validGroups[i];
    const junction = currentGroup.junction || "and";

    if (junction === "or") {
      // Wrap previous result and new group condition in OR
      result = {
        type: "or" as const,
        conditions: [result, groupCondition],
      } as OrCondition & { type: "or" };
    } else {
      // Wrap previous result and new group condition in AND
      result = {
        type: "and" as const,
        conditions: [result, groupCondition],
      } as AndCondition & { type: "and" };
    }
  }

  return result;
};

export const getValidationError = (
  selectedFieldType: PropertyDefinitionOutputTypeEnum | undefined,
  fieldValue: string,
): string | undefined => {
  // Skip validation for empty values (handled by required validation)
  if (!fieldValue || !selectedFieldType) {
    return undefined;
  }

  const trimmedValue = fieldValue.trim();

  // Validate based on field type
  switch (selectedFieldType) {
    case PropertyDefinitionOutputTypeEnum.NUMBER: {
      const error = validateNumber(trimmedValue);
      return error?.message;
    }

    case PropertyDefinitionOutputTypeEnum.INTEGER: {
      const error = validateInteger(trimmedValue);
      return error?.message;
    }

    case PropertyDefinitionOutputTypeEnum.STRING: {
      const error = validateString(trimmedValue);
      return error?.message;
    }

    // Since we are only filtering on the id of the relation field, we validate that the value is a string
    case PropertyDefinitionOutputTypeEnum.RELATION: {
      const error = validateString(trimmedValue);
      return error?.message;
    }

    default:
      // For other types (ENUM, BOOLEAN, etc.), don't validate format here since we are using dropdowns for these fields
      return undefined;
  }
};

export const stringifyParams = (formData: EventFilterFormData): string => {
  try {
    return JSON.stringify(formData);
  } catch {
    return "";
  }
};

export const parseParams = (
  params: string,
): EventFilterFormData | undefined => {
  try {
    return JSON.parse(params) as EventFilterFormData;
  } catch {
    return undefined;
  }
};
